// การรักษา — the per-patient MONEY ledger.
//
// The money lens on the same rows Chart › History shows as the clinical lens:
// every visit, every billing line, what the patient paid, what ปกส./ประกัน still
// owes, and how the clinic/doctor split fell out. Read-only in this phase —
// editing lands behind the claude-group write endpoints (see
// docs/patient-treatments-plan.md).
//
// Everything comes from ONE upstream call: GET /treatment?type=patient already
// nests the full billing[] per visit (addon 21 `billing_treatment`, no output
// restriction, filtered active == true). No new backend was needed to read.

import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useOutletContext, useParams } from 'react-router-dom'
import {
  ClipboardList, ChevronDown, ChevronRight, AlertTriangle, Plus,
  FileText, Wallet, Pill,
} from 'lucide-react'
import { IconFiles } from '@tabler/icons-react'
import { apiGet, cachedGet } from '../../lib/api.js'
import { useMe } from '../../lib/me.js'
import { canManage, isDoctorOnly, canEditPastBilling, isToothTaxiTreatment, canEditSection } from '../../lib/role.js'
import BillingLineEditor from '../../components/billing/BillingLineEditor.jsx'
import TreatmentDrawer from '../../components/billing/TreatmentDrawer.jsx'
import AddTreatmentDrawer from '../../components/billing/AddTreatmentDrawer.jsx'
import { nameInitial } from '../../lib/text.js'
import { asList, buildChartIndex, getDoctorImage, parseDateObj } from '../../lib/chart-helpers.js'
import { isGeneratedDoc, docLabel, useDocumentIssuer, QUICK_DOC_KEYS } from '../../components/DocumentGenerator.jsx'
import { usePatientLive } from '../../lib/patientLive.js'
import { BillingTable } from '../../components/billing/BillingTable.jsx'
import { TreatmentActionBar } from '../../components/billing/TreatmentActionBar.jsx'
import { useTreatmentShortcuts } from '../../components/billing/useTreatmentShortcuts.jsx'
import MedLabelDialog, { isMedicationLine } from '../../components/billing/MedLabelDialog.jsx'
import {
  fetchAllTreatmentsMeta, sumLines, doctorName, paymentOf,
  isNoPay, TEAL, asArray, buildPmIndex, dayLocked,
} from '../../lib/billing.js'
import {
  PageHeader, Button, Select, Badge, Banner, Loading, EmptyState, NoMatchState, ErrorState,
  useToast, fmtMoney, fmtDate, dateParts, toISODate, TH_DAYS_SHORT, branchName,
} from '../../kit'

// Whole baht, rounded with Math.round BEFORE the kit formats it. lib/billing.js
// fmtBill rounds that way and BillingTable (the lines under each card) still uses
// it — fmtMoney alone rounds a negative half away from zero (-427.5 → -428 vs
// -427), and a card total that disagreed with its own table by one baht is the
// drift fmtBill's comment warns about. Unknown stays '—'.
const fmt = (n) => fmtMoney(n == null || n === '' ? n : Math.round(Number(n)))

// UI-only relabel — สนามเป้า is closed and superseded by อารีย์. Never rewrite
// branch_id at the data layer. Bare "อารีย์", matching PatientChartHistory.
// Only for a branch NAME the feed hands over; an id goes through kit branchName.
const relabelBranch = (name) => (/สนามเป้า/.test(name || '') ? 'อารีย์' : name)

// Cards start collapsed past this many visits, so a 100-visit ortho patient does
// not render 100 billing tables into the DOM at once.
const AUTO_OPEN = 3

export default function PatientTreatments() {
  const { id } = useParams()
  const { live, patient } = useOutletContext() || {}
  const me = useMe()
  const isManager = canManage(me)
  // PatientLayout already bounces doctor-only sessions to Chart, and both write
  // proxies refuse them server-side. This is the third, cheapest layer.
  const doctorOnlyView = isDoctorOnly(me)
  // The billing day lock — Admin only outside the visit's own Bangkok day.
  // NOT canManage(): the enforcing gate in functions/api/billing/_guard.js is
  // admin-only, and a manager shown an enabled button would meet a 403.
  const mayEditPast = canEditPastBilling(me)

  const [treatments, setTreatments] = useState(null)
  const [truncated, setTruncated] = useState(false)
  const [charts, setCharts] = useState([])
  const [branches, setBranches] = useState([])
  const [pmList, setPmList] = useState([])
  // The billing catalogue, for the one-click shortcuts: it is what names them
  // and prices them, so a retired or repriced type changes the button with no
  // deploy. cachedGet — the editor asks for the same list.
  const [btypes, setBtypes] = useState([])
  /* ค้างชำระ for THIS patient — from the endpoint, not summed off the rows this
     page already holds. Two reasons it is worth the extra call:
       · api 567 shares its where-clause with /reports/outstanding, so the figure
         at the counter and the figure on the chase list cannot disagree. Two
         different debts for one patient is the failure worth engineering against.
       · this page's own row set is CAPPED (`truncated`), and a silently short
         sum would understate a debt — the one direction that must never happen.
     null = not loaded / refused (a doctor-only session gets 403 and simply sees
     no alert; chasing payment is not their job). */
  const [debt, setDebt] = useState(null)
  const [error, setError] = useState(null)
  const [reloadKey, setReloadKey] = useState(0)
  const [editor, setEditor] = useState(null)   // {treatmentId, line}
  const [drawerId, setDrawerId] = useState(null) // treatment id shown in the detail drawer
  const [adding, setAdding] = useState(false)
  const toast = useToast()
  // Tracks which patient the current data belongs to. A plain `reloadKey > 0`
  // test is WRONG here: once any realtime bump has happened it stays true, so
  // navigating to another patient would skip the reset and show the PREVIOUS
  // patient's money until the new fetch resolved.
  const loadedFor = useRef(null)

  // Flat list of the patient's active promotion billing lines, for the redeem
  // panel. null while the history is still loading — an empty array there would
  // claim the packages are untouched.
  const promoRows = useMemo(() => (treatments === null ? null
    : treatments.flatMap((t) => asArray(t.billing)).filter((b) => Number(b?.promotion_patient_id) > 0)),
  [treatments])

  const [year, setYear] = useState('')
  const [doctor, setDoctor] = useState('')
  const [branch, setBranch] = useState('')
  const [sort, setSort] = useState('patient')
  const [open, setOpen] = useState(() => new Set())
  const [touchedOpen, setTouchedOpen] = useState(false)

  useEffect(() => {
    let alive = true
    // Blank the view only when the PATIENT changed. A realtime refetch replaces
    // the data in place — otherwise every expanded card collapses and the page
    // flashes its skeleton whenever a colleague saves a line.
    const patientChanged = loadedFor.current !== id
    loadedFor.current = id
    if (patientChanged) { setTreatments(null); setTouchedOpen(false); setOpen(new Set()) }
    setError(null); setTruncated(false)
    Promise.allSettled([
      fetchAllTreatmentsMeta(id),
      apiGet('/api/charts', { patient_id: id }),
    ]).then(([tRes, cRes]) => {
      if (!alive) return
      if (tRes.status === 'fulfilled') {
        setTreatments(tRes.value.items)
        setTruncated(tRes.value.truncated)
      } else {
        setError(tRes.reason?.message || 'โหลดข้อมูลการรักษาไม่สำเร็จ')
        setTreatments([])
      }
      setCharts(cRes.status === 'fulfilled' ? asList(cRes.value) : [])
    })
    return () => { alive = false }
  }, [id, reloadKey])  // eslint-disable-line react-hooks/exhaustive-deps



  useEffect(() => {
    cachedGet('/api/billing-types').then((r) => setBtypes(asList(r))).catch(() => {})
  }, [])

  // Re-read on every reload key: paying a debt off is a billing write like any
  // other, so the alert must clear the moment it is settled.
  useEffect(() => {
    if (!id) return
    let alive = true
    apiGet('/api/billing-outstanding-patient', { patient_id: id })
      .then((r) => { if (alive) setDebt(r) })
      .catch(() => { if (alive) setDebt(null) })
    return () => { alive = false }
  }, [id, reloadKey])

  useEffect(() => {
    cachedGet('/api/branches').then((r) => setBranches(asList(r))).catch(() => {})
    cachedGet('/api/payment-methods').then((r) => setPmList(asList(r))).catch(() => {})
  }, [])

  // Live: billing lines, the treatment row itself, and promotion steps (a sold
  // package moves the balance shown on the cards). The events come off the ONE
  // `patient_update/{id}` subscription in PatientLayout via the outlet context.
  //
  // This replaced two subscriptions that could never fire: `patient/{id}` (the
  // Bubble channel — it refuses anonymous clients, and this app never sets a
  // realtime auth token, so it never connected) and `treatment_update` (create
  // only, so an edit or a delete at another station never arrived). The table
  // triggers cover every write path including the Bubble app's own.
  //
  // Debounced in usePatientLive because autofeeBilling can rewrite sibling lines
  // in the SAME user action: without it, one save at another station triggers
  // several full history walks and every expanded card snaps shut mid-read.
  usePatientLive(live, ['billing', 'treatment', 'promotion'], () => setReloadKey((k) => k + 1), 500)

  // Shared with /treatments — a narrower fallback chain here would resolve some
  // methods to '' and fire the red "ไม่ระบุวิธีชำระ" badge on visits that page
  // renders correctly.
  const pmById = useMemo(() => buildPmIndex(pmList), [pmList])

  // A สนามเป้า (1) row reads as อารีย์ either way — kit branchName for an id,
  // relabelBranch for a name the feed already resolved — so the branch filter
  // below, which matches by NAME, covers อารีย์ + สนามเป้า as decision #2 asks.
  const branchNameOf = useMemo(() => (t) => {
    const direct = (typeof t?.branch === 'object' ? t?.branch?.name : null) || t?.branch_name
    if (direct) return relabelBranch(direct)
    const bid = t?.branch_id ?? (typeof t?.branch === 'object' ? t?.branch?.id : t?.branch)
    if (bid == null || bid === '') return ''
    return branchName(bid, branches)
  }, [branches])

  const chartIndex = useMemo(() => buildChartIndex(charts || []), [charts])

  const all = treatments || []

  // filter option lists, derived from what this patient actually has
  const years = useMemo(() => [...new Set(all.map((t) => { const d = parseDateObj(t.date); return d ? String(d.getFullYear()) : '' }).filter(Boolean))].sort().reverse(), [all])
  const doctors = useMemo(() => {
    const m = new Map()
    for (const t of all) { const n = doctorName(t); if (n) m.set(n, (m.get(n) || 0) + 1) }
    return [...m.entries()].sort((a, b) => b[1] - a[1])
  }, [all])
  const branchOpts = useMemo(() => {
    const m = new Map()
    for (const t of all) { const n = branchNameOf(t); if (n) m.set(n, (m.get(n) || 0) + 1) }
    return [...m.entries()].sort((a, b) => b[1] - a[1])
  }, [all, branchNameOf])

  const filtered = useMemo(() => all.filter((t) => {
    if (year) { const d = parseDateObj(t.date); if (!d || String(d.getFullYear()) !== year) return false }
    if (doctor && doctorName(t) !== doctor) return false
    if (branch && branchNameOf(t) !== branch) return false
    return true
  }), [all, year, doctor, branch, branchNameOf])

  // Lifetime money.
  //
  // There used to be a "ยอด รวม เป็นยอดสุทธิ — มีรายการคืนเงิน/ปรับปรุง X บาท" note
  // here, computed as the sum of every negative priceXunit. Removed 2026-08-13:
  // checked against a real record, 6,500 of one patient's 6,700 was ortho coupon
  // discounts and a promotional freebie, and only 200 was an actual refund. Those
  // discounts are printed on the patient's receipt too, so the note warned about a
  // mismatch that does not exist — on the page whose premise is that its total is
  // authoritative. A negative line is not evidence of a reversal.
  const summary = useMemo(() => {
    const lines = filtered.flatMap((t) => asArray(t.billing))
    const s = sumLines(lines)
    const dates = filtered.map((t) => toISODate(t.date)).filter(Boolean).sort()
    return { ...s, visits: filtered.length, first: dates[0], last: dates[dates.length - 1] }
  }, [filtered])

  const groups = useMemo(() => groupByYear(filtered), [filtered])

  // Default-open the most recent few; once the user touches a card, respect them.
  const ordered = useMemo(() => groups.flatMap((g) => g.items), [groups])
  const effectiveOpen = useMemo(() => {
    if (touchedOpen) return open
    const s = new Set()
    ordered.slice(0, AUTO_OPEN).forEach((t) => s.add(String(t.id ?? t.treatment_id)))
    return s
  }, [touchedOpen, open, ordered])

  const toggle = (tid) => {
    setTouchedOpen(true)
    setOpen((prev) => {
      const base = touchedOpen ? prev : effectiveOpen
      const next = new Set(base)
      if (next.has(tid)) next.delete(tid); else next.add(tid)
      return next
    })
  }
  const setAll = (openAll) => {
    setTouchedOpen(true)
    setOpen(openAll ? new Set(ordered.map((t) => String(t.id ?? t.treatment_id))) : new Set())
  }

  const loading = treatments === null
  const anyFilter = year || doctor || branch
  // Derived from the LIVE list, never snapshotted into state: a realtime refetch
  // (or a billing edit made inside the drawer) has to reach the open drawer, and
  // the drawer closes itself if the visit leaves the set.
  /* THE LIVE VERSION OF THE LINE THE BILLING EDITOR IS HOLDING.
   *
   * `editor.line` is a snapshot taken on the click that opened the drawer, and
   * nothing re-derives it — that is deliberate (re-seeding a form under someone
   * mid-edit is its own bug), so the drawer needs the current row beside the
   * snapshot to be able to say what moved. This page already refetches on the
   * `billing` realtime event, so this costs no request.
   *
   * THREE-VALUED, and the third value is the point:
   *   the row     → compare against it
   *   `null`      → the visit is loaded and this line is NOT in it, i.e. voided
   *                 at another station (the feed filters `active == true`)
   *   `undefined` → we cannot tell yet — still loading, or the visit is not in
   *                 this page's set. Says nothing rather than crying "deleted".
   */
  const editorFreshLine = useMemo(() => {
    const lineId = editor?.line?.id
    if (!lineId || treatments === null) return undefined
    const t = all.find((x) => String(x.id ?? x.treatment_id) === String(editor.treatmentId))
    if (!t) return undefined
    return asArray(t.billing).find((b) => Number(b?.id) === Number(lineId)) ?? null
  }, [editor, all, treatments])

  const drawerTreatment = useMemo(
    () => (drawerId ? all.find((t) => String(t.id ?? t.treatment_id) === String(drawerId)) : null) || null,
    [drawerId, all],
  )

  return (
    <div className="pb-16">
      <PageHeader
        level={2}
        title="การรักษา"
        meta={loading ? null : `${anyFilter ? `${filtered.length}/${all.length}` : all.length} ครั้ง`}
        actions={!doctorOnlyView && (
          <Button icon={Plus} onClick={() => setAdding(true)}>เพิ่มการรักษา</Button>
        )}
      />

      {loading && <Loading />}
      {!loading && error && (
        // Back to the spinner on retry: the failed load left `treatments` as [],
        // which would otherwise flash "ยังไม่มีประวัติ" while the retry runs.
        <ErrorState message={error} onRetry={() => { setTreatments(null); setReloadKey((k) => k + 1) }} />
      )}

      {!loading && !error && all.length === 0 && (
        <EmptyState title="ยังไม่มีประวัติการรักษา" />
      )}

      {!loading && !error && all.length > 0 && (
        <>
          {truncated && (
            <Banner tone="warn" className="mb-4">
              ประวัติยาวเกินกว่าที่ระบบดึงมาได้ทั้งหมด — ยอดรวมด้านล่างจึงยังไม่ครบ
            </Banner>
          )}

          <SummaryCard s={summary} debt={debt} />

          <Filters
            years={years} doctors={doctors} branches={branchOpts}
            year={year} setYear={setYear}
            doctor={doctor} setDoctor={setDoctor}
            branch={branch} setBranch={setBranch}
            anyFilter={anyFilter}
            onClear={() => { setYear(''); setDoctor(''); setBranch('') }}
            onExpandAll={() => setAll(true)}
            onCollapseAll={() => setAll(false)}
          />

          {filtered.length === 0 ? (
            <NoMatchState variant="card" onClear={() => { setYear(''); setDoctor(''); setBranch('') }} />
          ) : (
            <div className="space-y-6">
              {groups.map((g) => (
                <section key={g.key}>
                  <div className="flex items-center gap-3 mb-2.5 px-1">
                    <h3 className="text-[14px] font-semibold text-slate-700 whitespace-nowrap">
                      {g.year == null ? 'ไม่ระบุวันที่' : g.year}
                    </h3>
                    <span className="text-[13px] text-slate-400 font-medium whitespace-nowrap">{g.items.length} ครั้ง</span>
                    <div className="flex-1 h-px bg-slate-100" />
                    <span className="text-[13px] font-semibold tabular-nums" style={{ color: TEAL }}>
                      {fmt(sumLines(g.items.flatMap((t) => asArray(t.billing))).patient)}
                    </span>
                  </div>
                  <div className="space-y-2.5">
                    {g.items.map((t) => {
                      const tid = String(t.id ?? t.treatment_id)
                      return (
                        <TreatmentCard
                          key={tid}
                          patientId={id}
                          patient={patient}
                          me={me}
                          t={t}
                          branch={branchNameOf(t)}
                          pmById={pmById}
                          chart={chartIndex.findFor(t)}
                          isOpen={effectiveOpen.has(tid)}
                          onToggle={() => toggle(tid)}
                          onOpen={() => setDrawerId(tid)}
                          sort={sort}
                          onSort={setSort}
                          canWrite={!doctorOnlyView}
                          dayLocked={dayLocked(t.date, mayEditPast)}
                          btypes={btypes}
                          onAddLine={() => setEditor({ treatmentId: tid, txDate: t.date, line: null })}
                          onEditLine={(b) => setEditor({ treatmentId: tid, txDate: t.date, line: b })}
                          onChanged={() => setReloadKey((k) => k + 1)}
                        />
                      )
                    })}
                  </div>
                </section>
              ))}
            </div>
          )}
        </>
      )}

      {drawerTreatment && (
        <TreatmentDrawer
          patientId={id}
          t={drawerTreatment}
          chart={chartIndex.findFor(drawerTreatment)}
          branch={branchNameOf(drawerTreatment)}
          pmById={pmById}
          canWrite={!doctorOnlyView}
          isManager={isManager}
          onChanged={() => setReloadKey((k) => k + 1)}
          onClose={() => setDrawerId(null)}
        />
      )}

      {adding && (
        <AddTreatmentDrawer
          patientId={id}
          defaultBranchId={all[0]?.branch_id}
          branches={branches}
          onClose={() => setAdding(false)}
          onCreated={(res) => {
            setAdding(false)
            setReloadKey((k) => k + 1)
            // Which chart it landed in still has to be said — "where did my chart
            // go" is otherwise a support call (see AddTreatmentDrawer).
            toast.success('บันทึกแล้ว', {
              detail: res?.chart_created === false
                ? 'ใช้ chart เดิมของวันนั้น (คนไข้ หมอ สาขา วันเดียวกัน)'
                : 'สร้าง chart ใหม่ให้แล้ว',
            })
          }}
        />
      )}

      {editor && (
        <BillingLineEditor
          /* Remounted by ใช้ค่าล่าสุด, and only by it: every input in the editor
             is seeded from `line` by useState, so re-seeding them means starting
             the component again. `rev` is bumped by onTakeLatest below. */
          key={`${editor.line?.id ?? 'new'}:${editor.rev || 0}`}
          patientId={id}
          treatmentId={editor.treatmentId}
          /* The โปรโมชั่น mode needs to know WHICH installments of a package are
             already billed. That is this page's own data: every active billing row
             of every visit, which is byte-for-byte the row set api 189 counts. No
             endpoint is involved. `truncated` means the history walk hit its cap,
             so the list is incomplete and must not be read as "these are free". */
          promoRows={promoRows}
          promoComplete={!truncated}
          /* The VISIT's day — what the billing day lock is judged on. A new
             line has no date of its own yet, and an existing one takes its
             `date` from this treatment anyway. */
          txDate={editor.txDate}
          line={editor.line}
          /* The same row as the ledger now shows — the drawer warns on the
             difference between this and its own snapshot. */
          freshLine={editorFreshLine}
          onTakeLatest={editorFreshLine
            ? () => setEditor((e) => ({ ...e, line: editorFreshLine, rev: (e.rev || 0) + 1 }))
            : undefined}
          onClose={() => setEditor(null)}
          /* `keepOpen` is the partial-redeem case: some installments of a
             promotion were written and some were refused, so the ledger must
             reload while the drawer stays up to say which ones failed. */
          onSaved={(opts) => { if (!opts?.keepOpen) setEditor(null); setReloadKey((k) => k + 1) }}
          onDeleted={() => { setEditor(null); setReloadKey((k) => k + 1) }}
        />
      )}

    </div>
  )
}

/* -------------------------------------------------------------------------- */

function SummaryCard({ s, debt }) {
  const [openDebt, setOpenDebt] = useState(false)
  // Only a POSITIVE balance is a debt. 0 means settled and ≤0 cannot happen for
  // a real ค้างชำระ — the endpoint drops patients who owe nothing, so anything
  // else here is noise and must not raise an alarm at the counter.
  const owed = Number(debt?.outstanding) || 0
  const debtLines = Array.isArray(debt?.items) ? debt.items : []

  return (
    <div className="mb-4 rounded-2xl border border-slate-100 bg-white p-4 sm:p-5">
      <div className="flex items-baseline gap-2 mb-3">
        <h3 className="text-[14px] font-semibold text-slate-800">สรุปตลอดการรักษา</h3>
        <span className="text-[12.5px] text-slate-400">
          {s.visits} ครั้ง{s.first && ` · ${fmtDate(s.first)} – ${fmtDate(s.last)}`}
        </span>
      </div>

      <div className="flex flex-wrap gap-x-8 gap-y-3">
        <Stat label="รวมเบิก" value={s.total} strong />
        <Stat label="รวม" value={s.patient} color={TEAL} strong />
        <Stat label="เบิก" value={s.advance} color="#4f46e5" />
        {/* Every employee sees the clinic/doctor split (Ram, 2026-10-13). It used to
            be manager-only here, while the per-line คลินิก/หมอ columns in the cards
            below were already open to everyone — so the gate hid a sum of figures
            the same screen printed line by line. */}
        <Stat label="ส่วนคลินิก" value={s.clinic} />
        <Stat label="ส่วนหมอ" value={s.doctor} />
      </div>

      {/* ค้างชำระ. NOT a sixth Stat: the five above are what this patient has
          been billed over their life, all facts of the past. This is money owed
          RIGHT NOW, it is the only figure on the card anyone has to act on, and
          at the counter it has to be impossible to read past — so it gets its own
          band and the only warning colour on the page. Absent entirely when the
          patient owes nothing, so its presence alone means "collect". */}
      {owed > 0 && (
        <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50/70 overflow-hidden">
          <button type="button" onClick={() => setOpenDebt((v) => !v)}
            className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-left hover:bg-rose-50">
            <AlertTriangle size={16} className="flex-none text-rose-500" />
            <span className="text-[13.5px] font-semibold text-rose-700">ค้างชำระ</span>
            <span className="text-[18px] font-bold tabular-nums text-rose-700 leading-none">{fmt(owed)}</span>
            <span className="text-[12.5px] text-rose-400">
              {debt?.lines ?? debtLines.length} รายการ
            </span>
            <span className="ml-auto inline-flex items-center gap-1 text-[12.5px] text-rose-500">
              {openDebt ? 'ซ่อน' : 'ดูรายการ'}
              <ChevronDown size={13} className={openDebt ? 'rotate-180 transition-transform' : 'transition-transform'} />
            </span>
          </button>

          {openDebt && (
            <div className="border-t border-rose-200/70 bg-white">
              {debtLines.length === 0 ? (
                <div className="px-3.5 py-2.5 text-[12.5px] text-slate-400">ไม่พบรายละเอียด</div>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {debtLines.map((b) => <DebtLine key={b.id} b={b} />)}
                </ul>
              )}
              <p className="px-3.5 py-2 text-[11.5px] text-slate-400 leading-relaxed border-t border-slate-100">
                แสดงตามค่าที่บันทึกไว้จริง · ยอดติดลบคือรายการที่ค้างชำระ ·
                ยอดบวกคือเงินที่คนไข้ชำระคืนแล้ว · ผลรวมทุกรายการคือยอดค้างชำระด้านบน
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/* One ค้างชำระ line, PRINTED AS RECORDED — no sign flipping. A negative
   priceXunit is the debt itself, a positive one is money paid back (owner's
   rule, 2026-08-13, and the same rule OutstandingDrawer follows). The headline
   above is the one deliberate exception: it is -(sum of these), which reads
   naturally as an amount to collect. Both surfaces take that figure from api 567
   rather than re-summing, so the counter and the chase list cannot drift. */
function DebtLine({ b }) {
  const x = Number(b.priceXunit) || 0
  const isCharge = x < 0
  const qty = Number(b.N) || 1
  return (
    <li className="px-3.5 py-2 flex items-start gap-3">
      <div className="min-w-0 flex-1">
        <div className="text-[13px] text-slate-800 leading-snug">
          {b.detail?.trim() || b.type_name || 'ค้างชำระ'}
        </div>
        <div className="text-[11.5px] text-slate-400 mt-0.5 flex items-center gap-1.5 flex-wrap">
          <span>{fmtDate(b.tx_date || b.date)}</span>
          {qty > 1 && <span>· ×{qty}</span>}
          {b.billing_type_id === 153 && <span>· ปรับปรุง</span>}
        </div>
      </div>
      <div className="text-right shrink-0">
        <div className={`text-[13.5px] font-semibold tabular-nums ${isCharge ? 'text-slate-900' : 'text-emerald-600'}`}>{fmt(x)}</div>
        <div className={`text-[10.5px] ${isCharge ? 'text-rose-500' : 'text-emerald-500'}`}>
          {isCharge ? 'ค้างชำระ' : 'ชำระคืน'}
        </div>
      </div>
    </li>
  )
}

function Stat({ label, value, color, strong }) {
  return (
    <div>
      <div className="text-[12px] text-slate-400 mb-0.5">{label}</div>
      <div className={`tabular-nums ${strong ? 'text-[18px] font-bold' : 'text-[16px] font-semibold'}`}
        style={{ color: color || '#0f172a' }}>{fmt(value)}</div>
    </div>
  )
}

function Filters({
  years, doctors, branches, year, setYear, doctor, setDoctor, branch, setBranch,
  anyFilter, onClear, onExpandAll, onCollapseAll,
}) {
  const ref = useRef(null)
  const [stuck, setStuck] = useState(false)

  // The bar reads as an inset card in the flow (same edges as สรุปตลอดการรักษา)
  // and only goes full-bleed once it pins under the header. A sticky element
  // exposes no "am I pinned?" state, so compare its live top against its own
  // resolved `top` — reading the computed value covers the calc() and the lg
  // breakpoint for free. The scroller is <main>, not the window, so the scroll
  // listener sits on document in the capture phase (scroll doesn't bubble).
  useEffect(() => {
    const el = ref.current
    if (!el) return
    let frame = 0
    const measure = () => {
      frame = 0
      const top = parseFloat(getComputedStyle(el).top) || 0
      setStuck(el.getBoundingClientRect().top <= top + 1)
    }
    const onScroll = () => { if (!frame) frame = requestAnimationFrame(measure) }
    measure()
    document.addEventListener('scroll', onScroll, { capture: true, passive: true })
    window.addEventListener('resize', onScroll)
    return () => {
      if (frame) cancelAnimationFrame(frame)
      document.removeEventListener('scroll', onScroll, { capture: true })
      window.removeEventListener('resize', onScroll)
    }
  }, [])

  return (
    // Offsets from the measured patient header (--ph) like every other sticky
    // element in the patient module; the 3.5rem is the mobile app bar.
    <div
      ref={ref}
      className={`sticky top-[calc(3.5rem+var(--ph,5.5rem))] lg:top-[var(--ph,5.5rem)] z-10 py-2 mb-4 ${
        stuck
          ? '-mx-4 sm:-mx-6 lg:-mx-10 px-4 sm:px-6 lg:px-10 bg-white/90 backdrop-blur supports-[backdrop-filter]:bg-white/75 border-y border-slate-200/70'
          : 'px-3 sm:px-4 rounded-2xl border border-slate-100 bg-white'
      }`}
    >
      <div className="flex items-center gap-2 flex-wrap">
        {/* Kit <Select>s, not native ones. "ทุก…" is a real option with value ''
            (not the clear ✕), so a pick is undone the same way it was made. */}
        {years.length > 1 && (
          <Select className="w-[120px]" minMenuWidth={140} value={year} onChange={setYear}
            options={[{ value: '', label: 'ทุกปี' }, ...years.map((y) => ({ value: y, label: y }))]} />
        )}
        {doctors.length > 1 && (
          <Select className="w-[200px]" value={doctor} onChange={setDoctor}
            options={[{ value: '', label: 'ทุกหมอ' }, ...doctors.map(([n, c]) => ({ value: n, label: n, count: c }))]} />
        )}
        {branches.length > 1 && (
          <Select className="w-[170px]" value={branch} onChange={setBranch}
            options={[{ value: '', label: 'ทุกสาขา' }, ...branches.map(([n, c]) => ({ value: n, label: n, count: c }))]} />
        )}
        <button type="button" onClick={onClear} disabled={!anyFilter}
          className="inline-flex items-center gap-1 text-[12.5px] font-medium text-slate-500 hover:text-brand-700 disabled:opacity-40 disabled:pointer-events-none">
          ล้างตัวกรอง
        </button>
        <div className="ml-auto flex items-center gap-2 text-[12.5px]">
          <button onClick={onExpandAll} className="text-slate-500 hover:text-brand-700">ขยายทั้งหมด</button>
          <span className="text-slate-200">|</span>
          <button onClick={onCollapseAll} className="text-slate-500 hover:text-brand-700">ย่อทั้งหมด</button>
        </div>
      </div>
    </div>
  )
}

function TreatmentCard({ patientId, patient, me, t, branch, pmById, chart, isOpen, onToggle, onOpen, sort, onSort, canWrite, dayLocked: locked, btypes, onAddLine, onEditLine, onChanged }) {
  const tid = t.id ?? t.treatment_id
  const lines = asArray(t.billing)
  const s = sumLines(lines)
  const pay = paymentOf(t, pmById)
  const noPay = isNoPay(pay.key)
  const al = t.appointmentlist || t.appointmentList || {}
  const alType = al.type || t.type || ''
  const docName = doctorName(t)
  const docImg = getDoctorImage(t)
  const d = fmtCardDate(t.date)
  // The rule: a visit with no payment method must have รวม (out-of-pocket) of 0.
  // Anything else means cash was taken and never attributed — advance-only visits
  // (ปกส. covers the lot) have รวม 0 and so never flag. Drives the PayChip's tone.
  const payRisk = noPay && Math.abs(s.patient) > 0.5

  /* Every one-click preset of this visit — buttons, dialogs and state — lives in
     useTreatmentShortcuts. The card only says WHERE they go. Tooth Taxi rates
     exist only on a Tooth Taxi visit, so the branch is resolved here (it can sit
     on the treatment, its appointment or the chart) and the hook decides. */
  const shortcuts = useTreatmentShortcuts({
    treatmentId: tid,
    lines,
    types: btypes,
    isToothTaxi: isToothTaxiTreatment(t, chart),
    onChanged,
  })

  /* ออกเอกสาร — the chart's Documents flow (same dialogs, same preview, same
     attach to chart.document[]), reachable from here so issuing a ใบเสร็จ does
     not mean opening the chart first. NOT a `write` action: a document is not a
     billing line, so the billing day lock does not apply — the chart's own
     'documents' permission does, exactly as on the chart page. */
  const issuer = useDocumentIssuer({
    treatment: t, chart, patient, hn: patient?.hn || patient?.HN || '', onSaved: onChanged,
  })
  // ใบเสร็จ / ใบรับรองแพทย์ sit at the menu's first level; every other document
  // is one step down, under เอกสารอื่นๆ.
  const canIssue = canEditSection('documents', me, t, chart)
  const noChart = !chart?.id
  const docItem = (def) => ({
    key: `doc:${def.key}`, label: def.label, icon: def.icon,
    onClick: () => issuer.open(def),
    disabled: noChart || issuer.busy,
    hint: noChart ? 'ยังไม่มี chart ในเคสนี้ — ออกเอกสารไม่ได้' : undefined,
  })
  const quickDefs = issuer.visibleDefs.filter((def) => QUICK_DOC_KEYS.includes(def.key))
  const otherDefs = issuer.visibleDefs.filter((def) => !QUICK_DOC_KEYS.includes(def.key))
  const docActions = !canIssue ? [] : [
    ...quickDefs.map(docItem),
    ...(otherDefs.length ? [{
      key: 'documents', label: 'เอกสารอื่นๆ', icon: IconFiles,
      disabled: noChart || issuer.busy,
      hint: noChart ? 'ยังไม่มี chart ในเคสนี้ — ออกเอกสารไม่ได้' : undefined,
      items: otherDefs.map(docItem),
    }] : []),
  ]
  if (docActions[0]) docActions[0] = { ...docActions[0], divider: true }

  /* พิมพ์ฉลากยา — only on a visit that dispensed medication. Read-only (prints,
     writes nothing), so it is offered on a locked day and to doctors too. */
  const [medOpen, setMedOpen] = useState(false)
  const btById = useMemo(() => new Map(asArray(btypes).map((b) => [Number(b.id), b])), [btypes])
  const medLines = useMemo(() => lines.filter((b) => isMedicationLine(b, btById)), [lines, btById])
  const medActions = medLines.length ? [{
    key: 'medlabel', label: 'พิมพ์ฉลากยา', icon: Pill, divider: true,
    meta: `${medLines.length} รายการ`, onClick: () => setMedOpen(true),
  }] : []

  // Documents already issued for this visit. A saved receipt is permanent
  // evidence, so this LINKS the stored file — it never re-generates one.
  const docs = asArray(chart?.document).filter((doc) => isGeneratedDoc(doc))

  return (
    <div className="rounded-2xl border border-slate-100 bg-white overflow-hidden">
      <div className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50/60 transition-colors">
        {/* Two targets, deliberately: the chevron is the quick look (billing table
            in place) and the body is the full read (drawer). Same row, so the
            hit areas have to be separate elements — not one button doing both. */}
        <button onClick={onToggle} aria-expanded={isOpen} title={isOpen ? 'ย่อรายการ' : 'ดูรายการ Billing'}
          className="flex-none p-1 -ml-1 rounded text-slate-400 hover:text-brand-700 hover:bg-brand-50 transition-colors">
          {isOpen ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
        </button>
        <button onClick={onOpen} className="flex items-center gap-3 min-w-0 flex-1 text-left" title="เปิดรายละเอียดการรักษา">
          <div className="flex-none w-[112px] text-[13.5px] font-semibold text-slate-800 tabular-nums">{d}</div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 flex-wrap text-[13.5px]">
              {branch && <span className="text-slate-600">{branch}</span>}
              {docName && <><span className="text-slate-300">·</span><DoctorAvatar src={docImg} name={docName} /><span className="text-slate-700 font-medium">{docName}</span></>}
              {alType && <Badge>{alType}</Badge>}
              <PayChip name={pay.name} color={pay.color} none={noPay} error={payRisk} />
              {t.auto_fee === false && (
                <Badge title="ค่าธรรมเนียมบัตร/ประกันไม่ถูกคำนวณอัตโนมัติในเคสนี้">auto fee ปิด</Badge>
              )}
            </div>
            {/* รายละเอียด / หมายเหตุ on the header row, not in the expanded body:
                it is what tells one of a patient's three visits that day from the
                others, and it was invisible until you opened the card. TRUNCATED to
                one line on purpose — the drawer is where the full text is read. */}
            {(t.detail || t.remark) && (
              <div className="mt-0.5 text-[12px] text-slate-500 truncate" title={[t.detail, t.remark].filter(Boolean).join(' · ')}>
                {t.detail}
                {t.detail && t.remark && <span className="text-slate-300"> · </span>}
                {t.remark && <span className="text-slate-400">{t.remark}</span>}
              </div>
            )}
          </div>
          {/* รวม on top, รวมเบิก under it (owner's call, 2026-08-15). The two were
              the other way round: the bold figure was priceActualTotal, which on a
              ปกส. visit is mostly what the office will claim BACK, while the number
              the patient is standing there to pay sat in 11px underneath. */}
          <div className="text-right flex-none">
            <div className="text-[15px] font-bold tabular-nums text-slate-900">{fmt(s.patient)}</div>
            <div className="text-[11px] tabular-nums" style={{ color: TEAL }}>รวมเบิก {fmt(s.total)}</div>
          </div>
        </button>
        <Link
          to={`/patient/${patientId}/chart/history/${tid}`}
          state={{ treatment: t, chart }}
          title="เปิด Chart ของเคสนี้"
          aria-label="เปิด Chart ของเคสนี้"
          className="flex-none inline-flex items-center justify-center rounded-lg p-2 text-slate-400 hover:text-brand-700 hover:bg-brand-50 transition-colors"
        >
          <ClipboardList size={15} />
        </Link>
      </div>

      {isOpen && (
        <div className="border-t border-slate-100">
          <BillingTable
            lines={lines}
            sort={sort}
            onSort={onSort}
            showClaim
            onRowClick={canWrite ? onEditLine : null}
          />
          {/* Every shortcut this visit offers, in one row. Add a shortcut by
              adding an entry — `write: true` puts it under the same day-lock /
              canWrite rule as เพิ่มรายการ Billing, which the bar applies once
              for the whole row (see TreatmentActionBar for the rule itself).
              Rows still OPEN on a closed day; it is the WRITE that is refused,
              server-side, in functions/api/billing/_guard.js. */}
          <TreatmentActionBar
            canWrite={canWrite}
            locked={locked}
            actions={[
              { key: 'billing', label: 'เพิ่มรายการ Billing', icon: Plus, onClick: onAddLine, write: true, primary: true },
              ...shortcuts.actions,
              ...medActions,
              ...docActions,
            ]}
            status={shortcuts.status}
          />
          {issuer.err && (
            <div className="px-4 pb-2 text-[12.5px] text-rose-600 flex items-center gap-1.5">
              <AlertTriangle size={12} className="flex-none" /> {issuer.err}
            </div>
          )}
          {docs.length > 0 && (
            <div className="px-4 py-2.5 border-t border-slate-50">
              <div className="text-[11.5px] uppercase tracking-wide text-slate-400 mb-1.5">เอกสารในเคสนี้</div>
              <div className="flex flex-wrap gap-1.5">
                {docs.map((doc, i) => <DocChip key={i} doc={doc} />)}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Rendered outside the collapsible body: a dialog is a fixed overlay, and
          hanging it off a section that can unmount mid-write would take the
          confirmation down with it. */}
      {shortcuts.dialog}
      {medOpen && (
        <MedLabelDialog lines={medLines} treatment={t} patient={patient} branch={branch} onClose={() => setMedOpen(false)} />
      )}
      {issuer.dialogs}
    </div>
  )
}

// Links the stored PDF. Deliberately NOT a re-generate button: a saved ใบเสร็จ is
// permanent evidence, and the generator mints a NEW file + a new chart.document[]
// entry every time it runs.
function DocChip({ doc }) {
  const location = doc.location || doc.file?.path || ''
  const href = location ? `/api/files/${encodeKey(location)}` : (doc.url || '')
  const label = docLabel(doc)
  if (!href) return null
  return (
    <a href={href} target="_blank" rel="noreferrer"
      className="inline-flex items-center gap-1.5 rounded-lg bg-slate-50 ring-1 ring-slate-200 px-2 py-1 text-[12px] text-slate-600 hover:bg-brand-50 hover:text-brand-700 hover:ring-brand-200 transition-colors">
      <FileText size={12} />
      <span className="truncate max-w-[220px]">{label}</span>
      {/* Xano defaults an unset doc_number to 0 — React would print that 0. */}
      {doc.doc_number && String(doc.doc_number) !== '0' ? <span className="text-slate-400 font-mono text-[11px]">{doc.doc_number}</span> : null}
    </a>
  )
}

// ไม่ระบุ is only WRONG when money was actually collected from the patient.
// A visit whose รวม is 0 — nothing charged, or the whole bill covered by
// ปกส./ประกัน — legitimately has no payment method, so it stays muted. A visit
// with a non-zero รวม and no method is an unreconciled till, and the tag itself
// says so rather than sitting quietly next to a second warning badge.
function PayChip({ name, color, none, error }) {
  if (none) {
    return error ? (
      <Badge tone="rose" title="เก็บเงินจากคนไข้แล้วแต่ไม่ได้ระบุวิธีชำระ — ยอด “รวม” ควรเป็น 0 ถ้ายังไม่ได้เก็บเงิน">
        <AlertTriangle size={11} strokeWidth={2.2} />ไม่ระบุวิธีชำระ
      </Badge>
    ) : (
      <Badge tone="slate"><Wallet size={11} strokeWidth={2} />ไม่ระบุ</Badge>
    )
  }
  // The payment method's own colour from the data (kit Badge `color`).
  return <Badge color={color || '#059669'}><Wallet size={11} strokeWidth={2} />{name}</Badge>
}

function DoctorAvatar({ src, name }) {
  if (src) return <img src={src} alt="" className="w-4 h-4 rounded-full object-cover flex-none" referrerPolicy="no-referrer" />
  return (
    <span className="w-4 h-4 rounded-full bg-slate-100 text-slate-500 text-[9px] font-semibold flex items-center justify-center flex-none">
      {nameInitial(name)}
    </span>
  )
}

/* -------------------------------------------------------------------------- */

// Grouped by YEAR, not month: a patient with a visit or two a month turned the
// page into a column of one-row sections, and the header stopped being a
// heading and became noise. Within a year the visits stay newest-first, so the
// month is still obvious from each card's own date.
function groupByYear(list) {
  const m = new Map()
  // Rows whose date will not parse are NOT dropped: they are counted in the
  // lifetime totals, so silently omitting their cards would leave a total that
  // no visible line explains — on the page whose whole premise is that the
  // total is authoritative.
  const undated = []
  for (const t of list) {
    const d = parseDateObj(t.date)
    if (!d) { undated.push(t); continue }
    const y = d.getFullYear()
    const key = String(y)
    if (!m.has(key)) m.set(key, { key, year: y, items: [] })
    m.get(key).items.push(t)
  }
  const out = [...m.values()].sort((a, b) => b.year - a.year)
  if (undated.length) out.push({ key: 'undated', year: null, items: undated })
  return out
}

// "จ. 10/08/2026" on one line. Was a stacked day-number + weekday block, which
// read fine inside a month section but lost the month and year the moment the
// ledger started grouping by YEAR. Built from the kit's own parts (dateParts +
// fmtDate), so it is dd/mm/yyyy ค.ศ. like every other screen date.
function fmtCardDate(v) {
  const p = dateParts(v)
  return p ? `${TH_DAYS_SHORT[p.dow]} ${fmtDate(v)}` : '—'
}

function encodeKey(k) {
  return String(k).split('/').map(encodeURIComponent).join('/')
}
